import { insurerGuideTerms, policyholderGuideTerms } from "./guideInsurer.ts";
import type { Terms } from "./i18n";

/** Every key optional, at every depth: the terms that differ, and no others. */
export type TermOverrides<T> = {
  [K in keyof T]?: T[K] extends string ? string : TermOverrides<T[K]>;
};

/**
 * The terms of an insurer where they differ from a law firm's: a case is a
 * claim, its claims are heads of claim, a client is the insured, and of the
 * costs what is incurred is paid and what is estimated is outstanding. Typed
 * against the base terms, so a key that is not one of them does not compile.
 *
 * Figures that carry the sign of a party's net result (net result, expected
 * value) keep their names here: an insurer reads them as costs, which the
 * reserve views show (see docs/InsurerReserves.md).
 */
export const insurerTerms: TermOverrides<Terms> = {
  guide: insurerGuideTerms,
  appBar: {
    userProfile: {
      description:
        "Your account, the role you work in, the currency your amounts are shown in, and how you sign in.",
    },
  },
  home: {
    cases:
      "You have <bold>{{count}} claims</bold>, {{open}} of them not fully settled.",
    cases_one: "You have <bold>one claim</bold>.",
    noCases: "You have no claims yet. Create one or import one to begin.",
    newCase: "Add claim",
    search: "Go to a claim by its title or a party…",
    noMatch: "No claim matches",
    figures: { cases: "Claims" },
    section: { cases: { title: "Claims" } },
    casesArea:
      "Find a claim, carry on where you left off, and see where your claims stand.",
    portfolio: {
      description: "Your {{count}} claims taken together.",
      description_one: "Your one claim.",
    },
  },
  case: {
    parties: {
      description:
        "The parties to the claim, which of them are insured with you, and the assets each could pay from.",
      clientBadge: "Insured",
      section: { client: "Insured", noClient: "Other parties" },
      editDialog: {
        description:
          "Please specify the party's name. Further, a party can be marked as insured.",
        tip: { client: "Select if the party is insured with you" },
        client: "Party is insured",
      },
    },
    claims: { title: "Heads of claim" },
    timeline: {
      judgment: {
        claims: {
          title: "Judgment: heads of claim",
          description:
            "Each is decided per defendant, and a head of claim with issues issue by issue. Once all are decided, the amounts start from the claim's matching outcome.",
        },
        parts: { decisions: "a decision on each head of claim" },
      },
      intro:
        "When the claim started and how long you expect each stage to take. Only what you enter is planned: a stage without a duration has no expected end, and nor has any stage that counts from it.",
      date: { start: "Start of the claim" },
      explanation:
        "An expected end is the end of the stage before, or the start of the claim, plus the duration you entered. A closed stage keeps the date it ended on.",
    },
    details: {
      section: {
        configuration: { claims: "Heads of claim" },
        analytics: { outcomes: "Claim outcomes" },
      },
      caseId: "Claim '{{caseId}}'",
      delete: "Delete claim",
    },
  },
  PartyOverviewCard: {
    tooltip: { winChance: "Claim outcomes" },
  },
  reserve: {
    history: { field: { claims: "heads of claim" } },
  },
  agreement: {
    conditionalFeeAgreement: {
      configurator: { lost: { title: "If the claim is lost" } },
    },
  },
  CostProgressBar: {
    chip: {
      incurred: "Paid {{value}}",
      estimated: "Outstanding {{value}}",
    },
  },
  CaseOverview: {
    caseId: "Claim '{{caseId}}'",
    delete: "Delete claim",
    export: "Export claim",
    changeTitle: "Change claim title",
    shortcuts: {
      claims: {
        title_zero: "No heads of claim",
        title_one: "One head of claim",
        title_other: "{{count}} heads of claim",
      },
    },
    claims: {
      claimed_one: "Claimed by the insured",
      claimed_other: "Claimed by the insureds",
      defended_one: "Defended by the insured",
      defended_other: "Defended by the insureds",
      totalAmountsClaimed: "Total claimed",
    },
    headers: {
      parties: {
        clients: "{{count}} insureds",
        clients_one: "Insured",
        clients_zero: "No insured",
      },
    },
  },
  portfolio: {
    section: { clients: "Insureds" },
    description: {
      clients:
        "What the claims are worth to your insureds: their expected values and net results, the claimed amounts, the chances of winning, and the value at risk across the portfolio.",
    },
    caseCounter: "<bold>{{current}}</bold> of {{total}} claims",
    caseCounter_one: "<bold>{{current}}</bold> of 1 claim",
  },
};

/**
 * An insurer's terms in its own dispute with a policyholder, over its terms
 * for a third party's claim: the client party is the insurer itself.
 */
export const policyholderDisputeTerms: TermOverrides<Terms> = {
  guide: policyholderGuideTerms,
  reserve: {
    heads: { indemnity: "Policy benefit" },
    page: {
      noInsured: "The claim has no insurer among its parties: mark yourself.",
      scenariosBy: {
        heads:
          "The outcomes grouped by how many of the heads of claim we lose, each with its chance and its cost",
      },
    },
  },
  case: {
    parties: {
      description:
        "The parties to the claim: you, the policyholder, and the assets each could pay from.",
      clientBadge: "Insurer (us)",
      section: { client: "Insurer (us)" },
      editDialog: {
        description:
          "Please specify the party's name. Further, mark the party that is you, the insurer.",
        tip: { client: "Select if this party is you, the insurer" },
        client: "Party is the insurer (us)",
      },
    },
  },
  CaseOverview: {
    claims: {
      claimed_one: "Claimed by us",
      claimed_other: "Claimed by us",
      defended_one: "Defended by us",
      defended_other: "Defended by us",
    },
    headers: {
      parties: {
        clients: "Insurer (us)",
        clients_one: "Insurer (us)",
      },
    },
  },
};
